import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAP_URL = "https://map.hytale-id.net";

export function RealtimeMapModal({ isOpen, onClose }) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    setIframeLoaded(false);
    setShowFallback(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowFallback(true);
    }, 5000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setShowFallback(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-[80%] md:w-full max-w-6xl h-[80vh] md:h-[85vh] bg-card border border-border rounded-xl overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Realtime Map
                </h2>
                <p className="text-sm text-muted-foreground">
                  Explore the Hytale Indonesia world
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex-1 relative bg-muted">
              {!iframeLoaded && !showFallback && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground text-sm">Loading Map...</p>
                  </div>
                </div>
              )}

              {showFallback && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-muted">
                  <div className="text-center p-6">
                    <p className="text-foreground font-medium mb-2">
                      Map didn&apos;t load in iframe. Open directly.
                    </p>
                    <Button
                      asChild
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <a
                        href={MAP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        Open Map in New Tab
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              )}

              {!showFallback && (
                <iframe
                  src={MAP_URL}
                  style={{ width: "100%", height: "100%", border: 0 }}
                  allow="fullscreen"
                  onLoad={handleIframeLoad}
                  title="Hytale Indonesia Realtime Map"
                />
              )}
            </div>

            <div className="p-3 border-t border-border bg-card text-center">
              <a
                href={MAP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                If the map does not appear, click here to open directly
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
